import type { StackProps } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import type { BaseSyntheticEvent, JSX } from "react";
import { useState } from "react";
import BadgeDetached from "./BadgeDetached";
import ItemsListPopover from "./ItemsListPopover";
import TypographyTooltipEllipsis from "./TypographyTooltipEllipsis";

interface Props<T> {
  items: T[];
  propGetter: (item: T) => string;
  listItemPropGetter?: (item: T, index?: number) => string | JSX.Element;
  popupTitle?: string;
  displayCount?: number;
  sortComparer?: (a: string, b: string) => number;
  containerProps?: StackProps;
}

const renderProp = (prop: string | JSX.Element, index: number) => {
  if (typeof prop === "string") {
    return prop ? (
      <Typography noWrap key={index}>
        {prop}
      </Typography>
    ) : (
      "_"
    );
  }
  return prop;
};

const InlineItemsList = <T,>({
  items: initialItems,
  popupTitle,
  displayCount,
  listItemPropGetter,
  propGetter,
  sortComparer = (a, b) => a.localeCompare(b),
  containerProps = {},
}: Props<T>) => {
  const [popUpAnchorEl, setPopUpAnchorEl] = useState<HTMLElement | undefined>();

  const items = [...initialItems].sort((a, b) => sortComparer(propGetter(a), propGetter(b)));

  if (items.length === 0) {
    return <Typography>-</Typography>;
  }

  const toggleShowPopover = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setPopUpAnchorEl((prev) => (prev ? undefined : event.currentTarget));
  };

  const handleClosePopover = (event: BaseSyntheticEvent) => {
    event.stopPropagation();
    setPopUpAnchorEl(undefined);
  };

  const displayedCount = displayCount || 1;
  const displayItemTexts = items.slice(0, displayedCount).map(propGetter);
  const showCounter = items.length > displayedCount;

  return (
    <Stack
      direction="row"
      spacing={1}
      {...containerProps}
      sx={[
        { alignItems: "center", justifyContent: "flex-start", width: "100%", flexWrap: "nowrap" },
        ...(Array.isArray(containerProps.sx) ? containerProps.sx : [containerProps.sx]),
      ]}
    >
      <TypographyTooltipEllipsis text={displayItemTexts.join(", ")} />
      {showCounter && (
        <BadgeDetached
          color="secondary"
          badgeContent={`+${items.length - displayedCount}`}
          onClick={toggleShowPopover}
          sx={{ cursor: "pointer" }}
        />
      )}
      <ItemsListPopover anchorEl={popUpAnchorEl} onClose={handleClosePopover}>
        <Stack spacing={1} sx={{ maxWidth: "25rem", p: 1 }}>
          {popupTitle && (
            <Typography color="textSecondary" variant="subtitle2">
              {popupTitle}
            </Typography>
          )}
          {items
            .slice(displayCount)
            .map((prop, index) => (listItemPropGetter ? listItemPropGetter(prop, index) : propGetter(prop)))
            .map((prop, index) => renderProp(prop, index))}
        </Stack>
      </ItemsListPopover>
    </Stack>
  );
};

export default InlineItemsList;
